import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import Tooltip from '@mui/material/Tooltip';
import { useBudgetState } from 'hooks/useBudgetState';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { DashboardEmptyState, RotatingWelcomeImage, AddButton } from 'pages/dashboard/dashboard-helpers';

const MYBPContainer = styled.div.attrs({
  className: 'MYBPContainer'
})`
  background: black;
  border-radius: 4px;
  color: white;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const MYBP = ({ isEmpty }) => {
  const { budgetState, updateBudgetState, addRevenueCategory, isLoading } = useBudgetState();

  // Separate UI state with collapsed default
  const [uiState, setUiState] = useState({
    collapsedCategories: {},
    collapsedProducts: {},
    hoveredProduct: null
  });

  // Effect to collapse all items when budgetState changes or loads
  useEffect(() => {
    if (budgetState?.categories) {
      setUiState(prev => {
        // Start with existing collapsed states
        const existingCategoryStates = prev.collapsedCategories;
        const existingProductStates = prev.collapsedProducts;
        
        // Ensure all categories have a collapsed state (defaulting to true for new ones)
        const newCategoryStates = budgetState.categories.reduce((acc, _, index) => {
          acc[index] = existingCategoryStates[index] !== undefined ? 
            existingCategoryStates[index] : true;  // Default to collapsed for new categories
          return acc;
        }, {});
        
        // Ensure all products have a collapsed state (defaulting to true for new ones)
        const newProductStates = budgetState.categories.reduce((acc, category, catIndex) => {
          category.products.forEach((_, prodIndex) => {
            const key = `${catIndex}-${prodIndex}`;
            acc[key] = existingProductStates[key] !== undefined ?
              existingProductStates[key] : true;  // Default to collapsed for new products
          });
          return acc;
        }, {});
        
        return {
          ...prev,
          collapsedCategories: newCategoryStates,
          collapsedProducts: newProductStates
        };
      });
    }
  }, [budgetState?.categories]);

  // Add state for tracking value changes
  const [recentChanges, setRecentChanges] = useState({});
  
  // Helper to trigger animation
  const triggerChangeAnimation = (categoryIndex, productIndex, year, type = 'product') => {
    const key = type === 'category' ? 
      `category-${categoryIndex}-${year}` : 
      `${categoryIndex}-${productIndex}-${year}`;
    
    setRecentChanges(prev => ({ ...prev, [key]: Date.now() }));
    setTimeout(() => {
      setRecentChanges(prev => {
        const newChanges = { ...prev };
        delete newChanges[key];
        return newChanges;
      });
    }, 300);
  };

  // Helper to reset to auto calculation
  const resetToAuto = (categoryIndex, productIndex, year) => {
    const newData = { ...budgetState };
    const product = newData.categories[categoryIndex].products[productIndex];
    
    if (year === 2) {
      product.isYear2Manual = false;
      product.unitsYear2 = product.unitsYear1 * 3;
      if (!product.isYear3Manual) {
        product.unitsYear3 = product.unitsYear1 * 9;
        triggerChangeAnimation(categoryIndex, productIndex, 3);
      }
    } else if (year === 3) {
      product.isYear3Manual = false;
      product.unitsYear3 = product.isYear2Manual ? 
        product.unitsYear2 * 3 : 
        product.unitsYear1 * 9;
    }
    
    triggerChangeAnimation(categoryIndex, productIndex, year);
    updateBudgetState(newData);
  };

  // useEffect(() => {
  //   const dbObject = {
  //     id: 'mock-mybp-1',
  //     user_id: 'mock-user-1',
  //     updated_at: new Date().toISOString(),
  //     data: {
  //       version: '1.0',
  //       last_modified: new Date().toISOString(),
  //       budget_data: budgetState,
  //       metadata: {
  //         summary: {
  //           total_categories: budgetState.categories.length,
  //           total_products: budgetState.categories.reduce((acc, cat) => acc + cat.products.length, 0),
  //           total_revenue: budgetState.categories.reduce((acc, cat) => {
  //             return acc + cat.products.reduce((prodAcc, prod) => {
  //               const year1 = prod.cost * prod.unitsYear1;
  //               const year2 = prod.cost * prod.unitsYear2;
  //               const year3 = prod.cost * prod.unitsYear3;
  //               return prodAcc + year1 + year2 + year3;
  //             }, 0);
  //           }, 0),
  //           revenue_by_year: budgetState.categories.reduce((acc, cat) => {
  //             cat.products.forEach(prod => {
  //               acc.year1 += prod.cost * prod.unitsYear1;
  //               acc.year2 += prod.cost * prod.unitsYear2;
  //               acc.year3 += prod.cost * prod.unitsYear3;
  //             });
  //             return acc;
  //           }, { year1: 0, year2: 0, year3: 0 }),
  //           manual_projections: budgetState.categories.reduce((acc, cat) => {
  //             cat.products.forEach(prod => {
  //               if (prod.isYear2Manual) acc.year2_count++;
  //               if (prod.isYear3Manual) acc.year3_count++;
  //             });
  //             return acc;
  //           }, { year2_count: 0, year3_count: 0 })
  //         }
  //       }
  //     }
  //   };

  //   console.log('MYBP Database Object Structure:', dbObject);
  // }, [budgetState]);

  const calculateRevenue = (cost, units) => cost * units;
  const calculateYear2 = (year1) => year1 * 3;
  const calculateYear3 = (year1) => year1 * 4;
  const calculateOverhead = (total) => total * 0.35;
  const calculateNet = (total, overhead) => total - overhead;

  const toggleProduct = (categoryIndex, productIndex) => {
    setUiState(prev => ({
      ...prev,
      collapsedProducts: {
        ...prev.collapsedProducts,
        [`${categoryIndex}-${productIndex}`]: !prev.collapsedProducts[`${categoryIndex}-${productIndex}`]
      }
    }));
  };

  const toggleCategory = (categoryIndex) => {
    setUiState(prev => ({
      ...prev,
      collapsedCategories: {
        ...prev.collapsedCategories,
        [categoryIndex]: !prev.collapsedCategories[categoryIndex]
      }
    }));
  };

  const addProduct = (categoryIndex) => {
    const newData = { ...budgetState };
    const newProductIndex = newData.categories[categoryIndex].products.length;
    newData.categories[categoryIndex].products.push({
      name: `Product ${newProductIndex + 1}`,
      cost: 0,
      unitsYear1: 0,
      unitsYear2: 0,
      unitsYear3: 0,
      isYear2Manual: false,
      isYear3Manual: false
    });
    
    // Set the new product as collapsed
    setUiState(prev => ({
      ...prev,
      collapsedProducts: {
        ...prev.collapsedProducts,
        [`${categoryIndex}-${newProductIndex}`]: true
      }
    }));
    
    updateBudgetState(newData);
  };

  const handleAddCategory = () => {
    // Add the new category
    addRevenueCategory();
  };

  const getTotalRevenue = (products) => {
    return products.reduce((acc, product) => {
      const year1 = calculateRevenue(product.cost, product.unitsYear1);
      const year2 = calculateRevenue(product.cost, product.unitsYear2);
      const year3 = calculateRevenue(product.cost, product.unitsYear3);
      return acc + year1 + year2 + year3;
    }, 0);
  };

  const deleteProduct = (categoryIndex, productIndex) => {
    const newData = { ...budgetState };
    newData.categories[categoryIndex].products.splice(productIndex, 1);
    updateBudgetState(newData);
  };

  const deleteCategory = (categoryIndex) => {
    const newData = { ...budgetState };
    newData.categories.splice(categoryIndex, 1);
    updateBudgetState(newData);
  };

  // Modify the onChange handlers to trigger category animations
  const handleProductChange = (categoryIndex, productIndex) => {
    // Trigger animations for all category metrics
    triggerChangeAnimation(categoryIndex, null, 'y1', 'category');
    triggerChangeAnimation(categoryIndex, null, 'y2', 'category');
    triggerChangeAnimation(categoryIndex, null, 'total', 'category');
    triggerChangeAnimation(categoryIndex, null, 'net', 'category');
    
    // Trigger animations for product metrics
    triggerChangeAnimation(categoryIndex, productIndex, 'total');
    triggerChangeAnimation(categoryIndex, productIndex, 'net');
  };

  return (
    <MYBPContainer>
      {(!budgetState?.categories || budgetState.categories.length === 0) ? (
        <DashboardEmptyState
          title="Welcome to the Revenue tab, a place to plan and track your revenue projections 💸."
          subtitle="Start planning your revenue streams."
          welcomeImage={<RotatingWelcomeImage />}
          actionButton={
            <AddButton 
              onClick={handleAddCategory}
              $shouldPulse={true}
              $color="#1CCCA4"
              $hoverColor="#19b894"
            >
              Add Revenue Category
            </AddButton>
          }
        />
      ) : (
        <CategoriesContainer>
          {budgetState.categories.map((category, categoryIndex) => {
            const totalRevenue = getTotalRevenue(category.products);
            const overhead = calculateOverhead(totalRevenue);
            return (
              <CategoryRow key={categoryIndex}>
                <CategoryHeader>
                  <CategoryNameSection>
                    <CollapseIcon
                      $isCollapsed={uiState.collapsedCategories[categoryIndex]}
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategory(categoryIndex);
                      }}
                    />
                    <CategoryName
                      value={category.name}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        const newData = { ...budgetState };
                        newData.categories[categoryIndex].name = e.target.value;
                        updateBudgetState(newData);
                      }}
                    />
                    <DeleteButton
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCategory(categoryIndex);
                      }}
                      $categoriesCount={budgetState.categories.length}
                    >
                      <DeleteOutlineIcon />
                    </DeleteButton>
                  </CategoryNameSection>
                  <CategoryMetrics>
                    <Metric>
                      <MetricLabel>Y1</MetricLabel>
                      <MetricValue className={recentChanges[`category-${categoryIndex}-y1`] ? 'value-changed' : ''}>
                        ${category.products.reduce((acc, prod) => acc + calculateRevenue(prod.cost, prod.unitsYear1), 0).toLocaleString()}
                      </MetricValue>
                    </Metric>
                    <Metric>
                      <MetricLabel>Y2</MetricLabel>
                      <MetricValue className={recentChanges[`category-${categoryIndex}-y2`] ? 'value-changed' : ''}>
                        ${category.products.reduce((acc, prod) => acc + calculateRevenue(prod.cost, prod.unitsYear2), 0).toLocaleString()}
                      </MetricValue>
                    </Metric>
                    <Metric>
                      <MetricLabel>3Y Total</MetricLabel>
                      <MetricValue className={recentChanges[`category-${categoryIndex}-total`] ? 'value-changed' : ''}>
                        ${totalRevenue.toLocaleString()}
                      </MetricValue>
                    </Metric>
                    <Metric>
                      <Tooltip title="3 Year Total minus 35% overhead" arrow placement="top">
                        <MetricLabel>Net Total</MetricLabel>
                      </Tooltip>
                      <MetricValue className={recentChanges[`category-${categoryIndex}-net`] ? 'value-changed' : ''}>
                        ${(totalRevenue - overhead).toLocaleString()}
                      </MetricValue>
                    </Metric>
                  </CategoryMetrics>
                </CategoryHeader>

                <ProductsContainer $isCollapsed={uiState.collapsedCategories[categoryIndex]}>
                  {category.products.map((product, productIndex) => {
                    const year1Revenue = calculateRevenue(product.cost, product.unitsYear1);
                    return (
                      <ProductRow 
                        key={productIndex}
                        onMouseEnter={() => setUiState(prev => ({ ...prev, hoveredProduct: productIndex }))}
                        onMouseLeave={() => setUiState(prev => ({ ...prev, hoveredProduct: null }))}
                      >
                        <ProductHeader onClick={() => toggleProduct(categoryIndex, productIndex)}>
                          <ProductNameSection>
                            <CollapseIcon $isCollapsed={uiState.collapsedProducts[`${categoryIndex}-${productIndex}`]} />
                            <ProductTitle>{product.name}</ProductTitle>
                          </ProductNameSection>
                          <ProductMetrics>
                            <Metric>
                              <MetricLabel>3Y Revenue</MetricLabel>
                              <MetricValue className={recentChanges[`${categoryIndex}-${productIndex}-total`] ? 'value-changed' : ''}>
                                ${(calculateRevenue(product.cost, product.unitsYear1) + 
                                  calculateRevenue(product.cost, product.unitsYear2) + 
                                  calculateRevenue(product.cost, product.unitsYear3)).toLocaleString()}
                              </MetricValue>
                            </Metric>
                            <Metric>
                              <Tooltip title="3 Year Total minus 35% overhead" arrow placement="top">
                                <MetricLabel>Net Profit</MetricLabel>
                              </Tooltip>
                              <MetricValue className={recentChanges[`${categoryIndex}-${productIndex}-net`] ? 'value-changed' : ''}>
                                ${((calculateRevenue(product.cost, product.unitsYear1) + 
                                  calculateRevenue(product.cost, product.unitsYear2) + 
                                  calculateRevenue(product.cost, product.unitsYear3)) * 0.65).toLocaleString()}
                              </MetricValue>
                            </Metric>
                          </ProductMetrics>
                        </ProductHeader>

                        <ProductContent $isCollapsed={uiState.collapsedProducts[`${categoryIndex}-${productIndex}`]}>
                          <ProductInputs>
                            <InputGroup>
                              <Label>Product Name</Label>
                              <NameInput
                                value={product.name}
                                onChange={(e) => {
                                  const newData = { ...budgetState };
                                  newData.categories[categoryIndex].products[productIndex].name = e.target.value;
                                  updateBudgetState(newData);
                                }}
                              />
                            </InputGroup>
                            <InputGroup>
                              <Label>Cost ($)</Label>
                              <StyledInput
                                type="number"
                                value={product.cost}
                                onFocus={(e) => {
                                  if (e.target.value === '0') {
                                    const newData = { ...budgetState };
                                    newData.categories[categoryIndex].products[productIndex].cost = '';
                                    updateBudgetState(newData);
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    const newData = { ...budgetState };
                                    newData.categories[categoryIndex].products[productIndex].cost = 0;
                                    updateBudgetState(newData);
                                  }
                                }}
                                onChange={(e) => {
                                  const newData = { ...budgetState };
                                  newData.categories[categoryIndex].products[productIndex].cost = e.target.value === '' ? '' : Number(e.target.value);
                                  triggerChangeAnimation(categoryIndex, productIndex, 1);
                                  updateBudgetState(newData);
                                  handleProductChange(categoryIndex, productIndex);
                                }}
                              />
                            </InputGroup>
                            <InputGroup>
                              <Label>Units Y1</Label>
                              <StyledInput
                                type="number"
                                value={product.unitsYear1}
                                onFocus={(e) => {
                                  if (e.target.value === '0') {
                                    const newData = { ...budgetState };
                                    newData.categories[categoryIndex].products[productIndex].unitsYear1 = '';
                                    updateBudgetState(newData);
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    const newData = { ...budgetState };
                                    newData.categories[categoryIndex].products[productIndex].unitsYear1 = 0;
                                    updateBudgetState(newData);
                                  }
                                }}
                                onChange={(e) => {
                                  const newData = { ...budgetState };
                                  const y1Value = e.target.value === '' ? '' : Number(e.target.value);
                                  const product = newData.categories[categoryIndex].products[productIndex];

                                  product.unitsYear1 = y1Value;

                                  if (!product.isYear2Manual && y1Value !== '') {
                                    product.unitsYear2 = y1Value * 3;
                                    triggerChangeAnimation(categoryIndex, productIndex, 2);
                                  }
                                  if (!product.isYear3Manual && y1Value !== '') {
                                    product.unitsYear3 = product.isYear2Manual ? product.unitsYear2 * 3 : y1Value * 9;
                                    triggerChangeAnimation(categoryIndex, productIndex, 3);
                                  }

                                  triggerChangeAnimation(categoryIndex, productIndex, 1);
                                  updateBudgetState(newData);
                                  handleProductChange(categoryIndex, productIndex);
                                }}
                              />
                              <YearMetric className={recentChanges[`${categoryIndex}-${productIndex}-1`] ? 'value-changed' : ''}>
                                ${(calculateRevenue(product.cost, product.unitsYear1)).toLocaleString()}
                              </YearMetric>
                            </InputGroup>
                            <InputGroup>
                              <InputHeader>
                                <Tooltip title={product.isYear2Manual ? "Using Custom Value" : "Triple Y1 Revenue. You can also use a custom value."} arrow placement="top">
                                  <Label>Y2</Label>
                                </Tooltip>
                                {product.isYear2Manual && (
                                  <ResetButton onClick={() => resetToAuto(categoryIndex, productIndex, 2)}>
                                    <RestartAltIcon />
                                  </ResetButton>
                                )}
                              </InputHeader>
                              <StyledInput
                                type="number"
                                value={product.unitsYear2}
                                onFocus={(e) => {
                                  if (e.target.value === '0') {
                                    const newData = { ...budgetState };
                                    newData.categories[categoryIndex].products[productIndex].unitsYear2 = '';
                                    updateBudgetState(newData);
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    const newData = { ...budgetState };
                                    const product = newData.categories[categoryIndex].products[productIndex];
                                    
                                    // Reset to non-custom state and recalculate
                                    product.isYear2Manual = false;
                                    product.unitsYear2 = product.unitsYear1 * 3;
                                    
                                    // If Y3 was based on Y2's custom value, reset it too
                                    if (!product.isYear3Manual) {
                                      product.unitsYear3 = product.unitsYear1 * 9;
                                    }
                                    updateBudgetState(newData);
                                  }
                                }}
                                onChange={(e) => {
                                  const newData = { ...budgetState };
                                  const product = newData.categories[categoryIndex].products[productIndex];

                                  if (e.target.value === '') {
                                    product.unitsYear2 = '';
                                  } else {
                                    const enteredValue = Number(e.target.value);
                                    const calculatedValue = product.unitsYear1 * 3;
                                    
                                    if (enteredValue === calculatedValue) {
                                      product.isYear2Manual = false;
                                    } else {
                                      product.isYear2Manual = true;
                                    }
                                    product.unitsYear2 = enteredValue;
                                    triggerChangeAnimation(categoryIndex, productIndex, 2);
                                  }

                                  if (!product.isYear3Manual && e.target.value !== '') {
                                    product.unitsYear3 = product.unitsYear2 * 3;
                                    triggerChangeAnimation(categoryIndex, productIndex, 3);
                                  }

                                  updateBudgetState(newData);
                                  handleProductChange(categoryIndex, productIndex);
                                }}
                              />
                              <YearMetric className={recentChanges[`${categoryIndex}-${productIndex}-2`] ? 'value-changed' : ''}>
                                ${(calculateRevenue(product.cost, product.unitsYear2)).toLocaleString()}
                              </YearMetric>
                            </InputGroup>
                            <InputGroup>
                              <InputHeader>
                                <Tooltip title={product.isYear3Manual ? "Using Custom Value" : "Triple Y2 Revenue. You can also use a custom value."} arrow placement="top">
                                  <Label>Y3</Label>
                                </Tooltip>
                                {product.isYear3Manual && (
                                  <ResetButton onClick={() => resetToAuto(categoryIndex, productIndex, 3)}>
                                    <RestartAltIcon />
                                  </ResetButton>
                                )}
                              </InputHeader>
                              <StyledInput
                                type="number"
                                value={product.unitsYear3}
                                onFocus={(e) => {
                                  if (e.target.value === '0') {
                                    const newData = { ...budgetState };
                                    newData.categories[categoryIndex].products[productIndex].unitsYear3 = '';
                                    updateBudgetState(newData);
                                  }
                                }}
                                onBlur={(e) => {
                                  if (e.target.value === '') {
                                    const newData = { ...budgetState };
                                    const product = newData.categories[categoryIndex].products[productIndex];
                                    
                                    // Reset to non-custom state and recalculate
                                    product.isYear3Manual = false;
                                    product.unitsYear3 = product.isYear2Manual ? 
                                      product.unitsYear2 * 3 : 
                                      product.unitsYear1 * 9;
                                    updateBudgetState(newData);
                                  }
                                }}
                                onChange={(e) => {
                                  const newData = { ...budgetState };
                                  const product = newData.categories[categoryIndex].products[productIndex];

                                  if (e.target.value === '') {
                                    product.unitsYear3 = '';
                                  } else {
                                    const enteredValue = Number(e.target.value);
                                    const calculatedValue = product.isYear2Manual ? 
                                      product.unitsYear2 * 3 : 
                                      product.unitsYear1 * 9;
                                    
                                    if (enteredValue === calculatedValue) {
                                      product.isYear3Manual = false;
                                    } else {
                                      product.isYear3Manual = true;
                                    }
                                    product.unitsYear3 = enteredValue;
                                    triggerChangeAnimation(categoryIndex, productIndex, 3);
                                  }

                                  updateBudgetState(newData);
                                  handleProductChange(categoryIndex, productIndex);
                                }}
                              />
                              <YearMetric className={recentChanges[`${categoryIndex}-${productIndex}-3`] ? 'value-changed' : ''}>
                                ${(calculateRevenue(product.cost, product.unitsYear3)).toLocaleString()}
                              </YearMetric>
                            </InputGroup>
                            <Badge 
                              $isHovered={uiState.hoveredProduct === productIndex}
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteProduct(categoryIndex, productIndex);
                              }}
                            >
                              <DeleteSweepIcon />
                            </Badge>
                          </ProductInputs>
                        </ProductContent>
                      </ProductRow>
                    );
                  })}
                  <AddProductButton onClick={() => addProduct(categoryIndex)}>
                    Add Product ?
                  </AddProductButton>
                </ProductsContainer>
              </CategoryRow>
            );
          })}
        </CategoriesContainer>
      )}
    </MYBPContainer>
  );
};


const CategoriesContainer = styled.div.attrs({
  className: 'CategoriesContainer'
})`
  display: flex;
  flex-direction: column;
  gap: 5px;
  flex: 1;
  overflow-y: scroll;
  width: 100%;
  font-family: PT Sans;
`;

const CategoryRow = styled.div.attrs({
  className: 'CategoryRow'
})`
  background: #1a1a1a;
  border-radius: 2px;
  display: flex;
  flex-direction: column;
`;

const CategoryHeader = styled.div.attrs({
  className: 'CategoryHeader'
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.$isCollapsed ? '0' : '0px'};
  padding: 12px 20px 10px 12px;
  user-select: none;
  &:hover {
    background: rgba(255,255,255,0.05);
    border-radius: 3px;
  }
`;

const CategoryNameSection = styled.div.attrs({
  className: 'CategoryNameSection'
})`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CollapseIcon = styled(KeyboardArrowDownIcon).attrs({
  className: 'CollapseIcon'
})`
  transition: transform 0.3s ease;
  transform: rotate(${props => props.$isCollapsed ? -90 : 0}deg);
  color: #B2BEB5;
`;

const CategoryName = styled.input.attrs({
  className: 'CategoryName'
})`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: white;
  background: transparent;
  border: none;
  padding: 0;
  width: 100%;
  font-family: PT Sans;
  text-decoration: underline;
  &:focus {
    outline: none;
  }
`;

const CategoryMetrics = styled.div.attrs({
  className: 'CategoryMetrics'
})`
  display: flex;
  gap: 24px;
`;

const Metric = styled.div.attrs({
  className: 'Metric'
})`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;

`;

const MetricLabel = styled.div.attrs({
  className: 'MetricLabel'
})`
  font-size: 12px;
  color: #B2BEB5;
`;

const MetricValue = styled.div.attrs({
  className: 'MetricValue'
})`
  font-size: 16px;
  font-weight: 600;
  color: #1CCCA4;
  transition: all 0.3s ease;
  
  &.value-changed {
    animation: metricPulse 0.3s ease;
  }

  @keyframes metricPulse {
    0% {
      color: #1CCCA4;
    }
    50% {
      color: #25f7c8;
    }
    100% {
      color: #1CCCA4;
    }
  }
`;

const ProductsContainer = styled.div.attrs({
  className: 'ProductsContainer'
})`
  display: flex;
  flex-direction: column;
  gap: 5px;
  overflow-y: auto;
  display: ${props => props.$isCollapsed ? 'none' : 'flex'};
  opacity: ${props => props.$isCollapsed ? '0' : '1'};
  max-height: ${props => props.$isCollapsed ? '0' : '400px'};
  margin: ${props => props.$isCollapsed ? '0' : '5px 0'};
  padding: 0 10px 5px;
  transition: all 0.3s ease-in-out;

  /* Customize scrollbar for webkit browsers */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }

  /* Customize scrollbar for Firefox */
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.2) rgba(255, 255, 255, 0.05);
`;

const ProductRow = styled.div.attrs({
  className: 'ProductRow'
})`
  background: rgba(255,255,255,0.02);
  border-radius: 3px;
  padding: 0px; 
  display: flex;
  flex-direction: column;
  gap: 0;
  animation: slideDown 0.3s ease-out;

  @keyframes slideDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ProductInputs = styled.div.attrs({
  className: 'ProductInputs'
})`
  display: flex;
  gap: 16px;
  padding-bottom: 12px;
  position: relative;
`;

const InputGroup = styled.div.attrs({
  className: 'InputGroup'
})`
  display: flex;
  flex-direction: column;
  gap: 4px;
  position: relative;
`;

const InputHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const ResetButton = styled.button`
  background: none;
  border: none;
  padding: 0px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  transition: opacity 0.2s;
  color: #B2BEB5;
  margin-top: -1px;

  &:hover {
    opacity: 1;
    color: #1CCCA4;
  }

  svg {
    font-size: 16px;
  }
`;

const Label = styled.label.attrs({
  className: 'Label'
})`
  font-size: 12px;
  color: #B2BEB5;
  margin-left: 5px;
`;

const Badge = styled.div.attrs({
  className: 'Badge'
})`
  color: ${props => props.$isHovered ? 'orange' : '#F2F2F2'};
  font-size: 12px;
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 3px;
  font-weight: 700;
  flex-shrink: 0;
  cursor: pointer;
  align-self: center;
  padding: 0px 8px;
  margin-left: auto;
  margin-right: 10px;
  margin-bottom: 10px;
  text-decoration: underline;
  text-underline-offset: 5px;
  &:hover {
    color: #ff4d4d;
  }
`;

const YearMetric = styled.div.attrs({
  className: 'YearMetric'
})`
  font-size: 13px;
  font-weight: 600;
  color: #17a589;
  margin-top: 4px;
  margin-left: 8px;
  transition: all 0.3s ease;
  
  &.value-changed {
    animation: pulse 0.3s ease;
  }

  @keyframes pulse {
    0% {
    }
    50% {
      color: #1CCCA4;
    }
    100% {
    }
  }
`;

const StyledInput = styled.input.attrs({
  className: 'StyledInput'
})`
  width: 50px;
  padding: 8px;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 4px;
  color: white;
  font-size: 13px;
  font-weight: 900;
  font-family: PT Sans;
  
  &:focus {
    outline: none;
    border-color: rgba(255,255,255,0.4);
  }

  /* Hide arrows for Chrome, Safari, Edge, Opera */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Hide arrows for Firefox */
  &[type='number'] {
    -moz-appearance: textfield;
  }
`;

const NameInput = styled(StyledInput)`
  width: 105px;
`;

const ProductHeader = styled.div.attrs({
  className: 'ProductHeader'
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  padding: 10px 11px 10px 5px;
  border-radius: 3px;
  position: relative;
  background: rgba(255,255,255,0.05);
  &:hover {
    background: rgba(255,255,255,0.05);
  }
`;

const ProductNameSection = styled.div.attrs({
  className: 'ProductNameSection'
})`
  display: flex;
  align-items: center;
  gap: 8px;
  svg {
    height: 20px;
  }
`;

const ProductTitle = styled.div.attrs({
  className: 'ProductTitle'
})`
  font-size: 14px;
  color: white;
  font-weight: 700;
`;

const ProductMetrics = styled.div.attrs({
  className: 'ProductMetrics'
})`
  display: flex;
  gap: 16px;

  ${MetricValue} {
    color: #17a589;
    font-size: 14px;
  }
`;

const ProductContent = styled.div.attrs({
  className: 'ProductContent'
})`
  visibility: ${props => props.$isCollapsed ? 'hidden' : 'visible'};
  opacity: ${props => props.$isCollapsed ? '0' : '1'};
  max-height: ${props => props.$isCollapsed ? '0' : '500px'};
  margin-top: ${props => props.$isCollapsed ? '0' : '10px'};
  padding: 0px 0px 0px 32px;
  overflow: hidden;
  transition: all 0.3s ease-in-out;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  border-radius: 4px;
  border: none;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  background: rgba(255,255,255,0.1);
  color: white;

  &:hover {
    background: rgba(255,255,255,0.2);
  }
`;

const AddProductButton = styled(Button)`
  background: none;
  text-decoration: underline;
  text-underline-offset: 5px;
  padding: 5px 10px;
  color: #17a589;
  font-size: 12px;
  font-weight: 700;
  font-family: PT Sans;
  &:hover {
    color: #1CCCA4;
    background: none;
  }
`;

const DeleteButton = styled.button.attrs({
  className: 'DeleteButton'
})`
  background: #2e2e2e;
  border: none;
  border-radius: 3px;
  color: #B2BEB5;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: all 0.2s ease;
  font-family: PT Sans;

  &:hover {
    color: #ff6b6b;
  }

  svg {
    font-size: 18px;
  }

  ${CategoryRow}:hover &,
  ${ProductRow}:hover & {
    opacity: 1;
  }
`;

export default MYBP; 